import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import PostForm from '../components/PostForm';
import PostCard from '../components/PostCard';
import { postApi } from '../services/postApi';
import { PageSkeleton } from '../components/Skeleton';
import SlowLoading from '../components/SlowLoading';
import './Feed.css';

const Feed = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [postComments, setPostComments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const limit = 20;

  // Batch load comments for all posts
  const loadBatchComments = async (postIds) => {
    if (!postIds || postIds.length === 0) return;
    try {
      const response = await postApi.getBatchComments(postIds);
      if (response.data) {
        setPostComments(prev => ({ ...prev, ...response.data }));
      }
    } catch (err) {
      console.error('Error loading batch comments:', err);
    }
  };

  const loadPosts = useCallback(async (reset = false) => {
    if (!reset && (loadingRef.current || !hasMoreRef.current)) return;
    loadingRef.current = true;
    if (!reset) setIsLoadingMore(true);

    try {
      setError('');
      const currentOffset = reset ? 0 : offsetRef.current;
      const response = await postApi.getFeed(limit, currentOffset);
      const newPosts = (response.data || []).filter(post => post && post.id);

      if (reset) {
        setPosts(newPosts);
        offsetRef.current = newPosts.length;
      } else {
        setPosts(prev => [...prev, ...newPosts]);
        offsetRef.current += newPosts.length;
      }

      const more = newPosts.length === limit;
      hasMoreRef.current = more;
      setHasMore(more);

      // Batch load comments for all posts
      if (newPosts.length > 0) {
        const postIds = newPosts.map(p => p.id).filter(id => id);
        loadBatchComments(postIds);
      }
    } catch (err) {
      console.error('Error loading feed:', err);
      setError(err.response?.data?.error || t('feed.loadError') || 'Failed to load feed');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      loadingRef.current = false;
    }
  }, [t]);

  useEffect(() => {
    loadPosts(true);
    window.scrollTo(0, 0);
  }, [loadPosts, location.key]);

  useEffect(() => {
    const handleScroll = () => {
      if (loadingRef.current || !hasMoreRef.current) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight - scrollBottom < 400) {
        loadPosts(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadPosts]);

  const handlePostCreated = useCallback(() => {
    hasMoreRef.current = true;
    setHasMore(true);
    loadPosts(true);
  }, [loadPosts]);

  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(post => post.id !== postId));
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(post =>
      post.id === updatedPost.id ? { ...post, ...updatedPost } : post
    ));
  };

  if (isLoading && posts.length === 0) {
    return (
      <div className="feed-container">
        <div className="feed-header">
          <h1>{t('feed.title') || 'Home'}</h1>
        </div>
        <PostForm />
        <PageSkeleton type="feed" count={3} />
      </div>
    );
  }

  return (
    <div className="feed-container fade-in">
      <div className="feed-header">
        <h1>{t('feed.title') || 'Home'}</h1>
      </div>

      <PostForm onPostCreated={handlePostCreated} />

      {error && <div className="error-message">{error}</div>}

      <SlowLoading
        isLoading={isLoadingMore}
        onRetry={() => loadPosts(false)}
        showSkeleton={false}
      >
        <div className="posts-list">
        {posts.length === 0 ? (
          <div className="empty-feed">
            <p>{t('feed.empty') || 'No posts yet. Be the first to share something!'}</p>
          </div>
        ) : (
          posts.filter(post => post && post.id).map(post => (
            <PostCard
              key={post.id}
              post={post}
              currentUserId={user?.id}
              initialComments={postComments[post.id]}
              onPostDeleted={handlePostDeleted}
              onPostUpdated={handlePostUpdated}
              onPostShared={handlePostCreated}
            />
          ))
        )}
        {isLoadingMore && (
          <div className="feed-loading-more">
            <div className="feed-spinner" />
          </div>
        )}
        {!isLoadingMore && !hasMore && posts.length > 0 && (
          <div className="feed-end">
            {t('feed.endOfFeed')}
          </div>
        )}
      </div>
      </SlowLoading>
    </div>
  );
};

export default Feed;
